import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Stethoscope, MessageSquare, Heart, Brain,
  Users, Eye, Zap, MapPin, Bus, Train,
  ArrowRight, Calendar, Check, Footprints,
} from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import { seo } from "../translations/seo";
import Hero from "../components/home/Hero";
import CentresPreview from "../components/home/CentresPreview";
import ContactSection from "../components/home/ContactSection";
import { useLanguage } from "../context/LanguageContext";
import { localizePost, DATE_LOCALES } from "../utils/blog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CENTRE_IMGS = [
  "/images/centre/centre-01.jpg",
  "/images/centre/centre-02.jpg",
  "/images/centre/centre-03.jpg",
  "/images/centre/centre-04.jpg",
  "/images/centre/centre-05.jpg",
  "/images/centre/centre-06.jpg",
  "/images/centre/centre-07.jpg",
  "/images/centre/centre-08.jpg",
  "/images/centre/centre-09.jpg",
  "/images/centre/centre-10.jpg",
  "/images/centre/centre-11.jpg",
];

const INTERIOR_IMGS = [
  CENTRE_IMGS[5], // outside / Psymed sign
  CENTRE_IMGS[0], // waiting room
  CENTRE_IMGS[7], // doctors consultation
  CENTRE_IMGS[2], // hallway
  CENTRE_IMGS[4], // reception area
];

const SERVICE_ICONS = [Stethoscope, MessageSquare, Heart, Footprints, Brain, Users, Eye, Zap];

// Maps each service (by id from translations.services.list) to the corresponding staff member id.
const SERVICE_TO_STAFF_ID = {
  1: 1, // Dermatology → Nakoutsi
  2: 3, // Logopédie → Ballatore
  3: 4, // Médecine Générale → Hendrickx
  4: 5, // Pédicure → Shimizu
  5: 8, // Psychologie 1ère ligne → Buchbinder
  6: 7, // Psychothérapie Systémique → Clemenza
  7: 6, // Psychanalyse / EMDR → Branchesi
  8: 2, // Laser Anti-Tabac → Reset Laser
};

function useScrollReveal(deps = []) {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add("visible"); }),
      { threshold: 0.12 }
    );
    document.querySelectorAll(".reveal").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, deps);
}

function formatDate(isoStr, lang) {
  if (!isoStr) return "";
  try {
    return new Date(isoStr).toLocaleDateString(DATE_LOCALES[lang] || "fr-BE", { day: "numeric", month: "long", year: "numeric" });
  } catch { return ""; }
}

export default function Home() {
  const { t, language } = useLanguage();
  const [posts, setPosts] = useState([]);
  const [currentPost, setCurrentPost] = useState(0);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [services, setServices] = useState([]);
  const [centres, setCentres] = useState([]);

  useScrollReveal([posts, services]);

  // Auto-advance blog carousel
  useEffect(() => {
    if (posts.length < 2) return;
    const timer = setInterval(() => {
      setCurrentPost((prev) => (prev + 1) % posts.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [posts.length]);

  const prevPost = () => setCurrentPost((prev) => (prev - 1 + posts.length) % posts.length);
  const nextPost = () => setCurrentPost((prev) => (prev + 1) % posts.length);

  // Gallery slideshow auto-rotate
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % CENTRE_IMGS.length);
    }, 4500);
    return () => clearInterval(timer);
  }, []);
  const prevSlide = () => setCurrentSlide((p) => (p - 1 + CENTRE_IMGS.length) % CENTRE_IMGS.length);
  const nextSlide = () => setCurrentSlide((p) => (p + 1) % CENTRE_IMGS.length);

  useEffect(() => {
    axios.get(`${API}/blog?limit=3`).then((r) => setPosts(r.data)).catch(() => {});
    axios.get(`${API}/services`).then((r) => setServices(r.data)).catch(() => {});
    axios.get(`${API}/centres`).then((r) => setCentres(r.data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-white font-jakarta">
      <Navbar />
      <Seo title={seo[language].home.title} description={seo[language].home.description} />

      <Hero />

      {/* ─── NEWS BANNER ──────────────────────────────────────────────────── */}
      <section className="bg-gradient-to-r from-[#2E4DB5] to-[#3B9DA5] py-6" data-testid="news-banner">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <div className="flex flex-col md:flex-row items-center gap-4 md:gap-8">
            <Link
              to="/actualites"
              className="flex-shrink-0 bg-white/20 text-white text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full hover:bg-white/30 transition-colors"
            >
              {t.news.title}
            </Link>
            <div className="flex flex-col md:flex-row gap-2 md:gap-6 text-sm text-white/90">
              <span className="flex items-center gap-2">
                <Check className="w-4 h-4 text-white/70 flex-shrink-0" />
                {t.news.blood_test}
              </span>
              <span className="hidden md:block text-white/40">|</span>
              <span className="flex items-center gap-2">
                <Check className="w-4 h-4 text-white/70 flex-shrink-0" />
                {t.news.new_center}
              </span>
            </div>
          </div>
        </div>
      </section>

      <CentresPreview centres={centres} />

      {/* ─── ABOUT ────────────────────────────────────────────────────────── */}
      <section id="centre" className="py-24 bg-white" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="reveal text-[#3B9DA5] text-sm font-semibold uppercase tracking-widest">
                {t.about.title}
              </span>
              <h2 className="reveal reveal-delay-1 font-outfit text-4xl sm:text-5xl font-bold text-slate-900 mt-3 mb-6 leading-tight">
                {t.about.subtitle}
              </h2>
              <p className="reveal reveal-delay-2 text-slate-600 leading-relaxed mb-4 text-base">
                {t.about.description}
              </p>
              <p className="reveal reveal-delay-2 text-slate-600 leading-relaxed mb-8 text-base">
                {t.about.description2}
              </p>
              <div className="reveal reveal-delay-3 grid grid-cols-2 gap-4">
                {[
                  { icon: Calendar, label: t.about.stat1 },
                  { icon: Users, label: t.about.stat2 },
                  { icon: Heart, label: t.about.stat3 },
                  { icon: MapPin, label: t.about.stat4 },
                ].map(({ icon: Icon, label }) => (
                  <div key={label} className="flex items-center gap-3 bg-slate-50 rounded-2xl px-4 py-3">
                    <Icon className="w-5 h-5 text-[#2E4DB5] flex-shrink-0" />
                    <span className="text-sm font-medium text-slate-700">{label}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="reveal reveal-delay-2 grid grid-cols-2 gap-3">
              <img
                src={INTERIOR_IMGS[0]}
                alt="Psymed interior"
                className="rounded-2xl w-full h-56 object-cover col-span-2"
              />
              <img
                src={INTERIOR_IMGS[1]}
                alt="Consultation room"
                className="rounded-2xl w-full h-40 object-cover"
              />
              <img
                src={INTERIOR_IMGS[2]}
                alt="Waiting area"
                className="rounded-2xl w-full h-40 object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ─── SERVICES ─────────────────────────────────────────────────────── */}
      <section id="services" className="py-24 bg-slate-50" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <div className="text-center mb-14">
            <span className="reveal text-[#3B9DA5] text-sm font-semibold uppercase tracking-widest">
              {t.services.subtitle}
            </span>
            <h2 className="reveal reveal-delay-1 font-outfit text-4xl sm:text-5xl font-bold text-slate-900 mt-3">
              {t.services.title}
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {(services.length > 0 ? services : t.services.list).map((svc, i) => {
              const localizedSvc = svc.i18n && svc.i18n[language]
                ? { ...svc, name: svc.i18n[language].name || svc.name, desc: svc.i18n[language].desc || svc.desc }
                : svc;
              const iconKey = svc.icon;
              const ICONS = { Stethoscope, MessageSquare, Heart, Footprints, Brain, Users, Eye, Zap };
              const Icon = (iconKey && ICONS[iconKey]) || SERVICE_ICONS[i % SERVICE_ICONS.length];
              const staffId = svc.staff_id ?? SERVICE_TO_STAFF_ID[svc.id];
              return (
                <div
                  key={svc.id}
                  className="reveal bg-white rounded-3xl p-6 border border-slate-100 shadow-sm hover:-translate-y-1 hover:shadow-lg transition-all duration-300 group flex flex-col"
                  style={{ transitionDelay: `${i * 0.05}s` }}
                  data-testid={`service-card-${svc.id}`}
                >
                  <div className="w-12 h-12 gradient-bg rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-outfit font-semibold text-slate-900 mb-2 leading-snug text-base">
                    {localizedSvc.name}
                  </h3>
                  <p className="text-slate-500 text-sm leading-relaxed flex-1">{localizedSvc.desc}</p>
                  <Link
                    to={staffId ? `/equipe#staff-${staffId}` : "/equipe"}
                    className="mt-4 text-[#2E4DB5] text-sm font-semibold flex items-center gap-1 hover:gap-2 transition-all"
                  >
                    {t.services.learn_more} <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── GALLERY ──────────────────────────────────────────────────────── */}
      <section className="py-14 bg-white" data-testid="gallery-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <div className="text-center mb-8">
            <span className="reveal text-[#3B9DA5] text-xs font-semibold uppercase tracking-widest">
              {t.gallery.title}
            </span>
            <h2 className="reveal reveal-delay-1 font-outfit text-2xl sm:text-3xl font-bold text-slate-900 mt-2">
              {t.gallery.subtitle}
            </h2>
          </div>
          <div className="reveal relative max-w-4xl mx-auto">
            {/* Slideshow */}
            <div className="relative aspect-[16/9] sm:aspect-[16/7] rounded-2xl overflow-hidden shadow-lg bg-slate-100">
              {CENTRE_IMGS.map((src, i) => (
                <img
                  key={src}
                  src={src}
                  alt={`Centre Psymed ${i + 1}`}
                  loading={i === 0 ? "eager" : "lazy"}
                  className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${
                    i === currentSlide ? "opacity-100" : "opacity-0"
                  }`}
                  data-testid={`gallery-slide-${i}`}
                />
              ))}

              {/* Prev / Next */}
              <button
                onClick={prevSlide}
                className="absolute left-3 sm:left-5 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white/85 hover:bg-white backdrop-blur shadow flex items-center justify-center text-slate-700"
                aria-label="Précédent"
                data-testid="gallery-prev"
              >
                <ArrowRight className="w-4 h-4 rotate-180" />
              </button>
              <button
                onClick={nextSlide}
                className="absolute right-3 sm:right-5 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white/85 hover:bg-white backdrop-blur shadow flex items-center justify-center text-slate-700"
                aria-label="Suivant"
                data-testid="gallery-next"
              >
                <ArrowRight className="w-4 h-4" />
              </button>

              {/* Counter pill */}
              <div className="absolute top-4 right-4 bg-slate-900/55 backdrop-blur text-white text-xs font-semibold px-3 py-1.5 rounded-full">
                {currentSlide + 1} / {CENTRE_IMGS.length}
              </div>
            </div>

            {/* Thumbnails */}
            <div className="mt-5 flex gap-2 overflow-x-auto pb-2 snap-x">
              {CENTRE_IMGS.map((src, i) => (
                <button
                  key={src}
                  type="button"
                  onClick={() => setCurrentSlide(i)}
                  className={`flex-shrink-0 w-20 h-14 sm:w-24 sm:h-16 rounded-xl overflow-hidden snap-start transition-all ${
                    i === currentSlide ? "ring-2 ring-[#2E4DB5] ring-offset-2" : "opacity-70 hover:opacity-100"
                  }`}
                  aria-label={`Aller à l'image ${i + 1}`}
                  data-testid={`gallery-thumb-${i}`}
                >
                  <img src={src} alt="" className="w-full h-full object-cover" loading="lazy" />
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ─── BLOG PREVIEW ─────────────────────────────────────────────────── */}
      <section className="py-12 bg-slate-50" data-testid="blog-preview-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <div className="flex items-end justify-between mb-8">
            <div>
              <span className="text-[#3B9DA5] text-xs font-semibold uppercase tracking-widest block mb-2">
                {t.blog.subtitle}
              </span>
              <h2 className="font-outfit text-3xl sm:text-4xl font-bold text-slate-900">
                {t.blog.title}
              </h2>
            </div>
            <Link
              to="/actualites"
              className="hidden sm:flex items-center gap-2 text-[#2E4DB5] font-semibold text-sm hover:gap-3 transition-all"
              data-testid="blog-view-all"
            >
              {t.blog.view_all} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          {posts.length === 0 ? (
            <p className="text-slate-500 text-center py-8">{t.blog.no_posts}</p>
          ) : (
            <div className="relative">
              {/* Carousel card */}
              <div className="overflow-hidden">
                {posts.map((rawPost, i) => {
                  const post = localizePost(rawPost, language);
                  return (
                  <Link
                    key={post.id}
                    to={`/actualites/${post.slug}`}
                    className={`transition-all duration-500 ${i === currentPost ? "block" : "hidden"} group`}
                    data-testid={`blog-card-${post.id}`}
                  >
                    <div className="bg-white rounded-3xl overflow-hidden border border-slate-100 shadow-sm hover:shadow-xl transition-shadow duration-300 grid md:grid-cols-2">
                      <div className="overflow-hidden h-40 md:h-56">
                        {post.image ? (
                          <img
                            src={post.image}
                            alt={post.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                        ) : (
                          <div className="w-full h-full gradient-bg opacity-20" />
                        )}
                      </div>
                      <div className="p-6 flex flex-col justify-center">
                        <span className="inline-block bg-blue-50 text-[#2E4DB5] text-xs font-semibold uppercase tracking-wide px-3 py-1 rounded-full mb-3 w-fit">
                          {post.category}
                        </span>
                        <h3 className="font-outfit font-bold text-slate-900 text-xl leading-snug mb-2">
                          {post.title}
                        </h3>
                        <p className="text-slate-500 text-sm leading-relaxed mb-4 line-clamp-2">{post.excerpt}</p>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-slate-400">{formatDate(post.published_at, language)}</span>
                          <span className="text-[#2E4DB5] text-sm font-semibold flex items-center gap-1 group-hover:gap-2 transition-all">
                            {t.blog.read_more} <ArrowRight className="w-4 h-4" />
                          </span>
                        </div>
                      </div>
                    </div>
                  </Link>
                  );
                })}
              </div>

              {/* Navigation */}
              {posts.length > 1 && (
                <div className="mt-6 flex items-center justify-center gap-4">
                  <button
                    onClick={prevPost}
                    className="w-10 h-10 rounded-full border border-slate-200 flex items-center justify-center text-slate-500 hover:bg-[#2E4DB5] hover:text-white hover:border-[#2E4DB5] transition-all"
                    data-testid="carousel-prev"
                  >
                    <ArrowRight className="w-5 h-5 rotate-180" />
                  </button>
                  <div className="flex gap-2">
                    {posts.map((_, i) => (
                      <button
                        key={i}
                        onClick={() => setCurrentPost(i)}
                        className={`rounded-full transition-all duration-300 ${
                          i === currentPost ? "w-6 h-2.5 bg-[#2E4DB5]" : "w-2.5 h-2.5 bg-slate-300 hover:bg-slate-400"
                        }`}
                        data-testid={`carousel-dot-${i}`}
                      />
                    ))}
                  </div>
                  <button
                    onClick={nextPost}
                    className="w-10 h-10 rounded-full border border-slate-200 flex items-center justify-center text-slate-500 hover:bg-[#2E4DB5] hover:text-white hover:border-[#2E4DB5] transition-all"
                    data-testid="carousel-next"
                  >
                    <ArrowRight className="w-5 h-5" />
                  </button>
                </div>
              )}
            </div>
          )}
          <div className="mt-8 sm:hidden text-center">
            <Link to="/actualites" className="text-[#2E4DB5] font-semibold text-sm">
              {t.blog.view_all} →
            </Link>
          </div>
        </div>
      </section>

      <ContactSection centres={centres} />

      <Footer />
    </div>
  );
}
